/**
 * Token Classification for Passphrase Analysis
 *
 * Classifies passphrase tokens into categories for entropy calculation
 * and user education. FILLER tokens (connectors) contribute near-zero
 * entropy and are visually de-emphasized in the UI.
 */

export type TokenType = 'WORD' | 'EMOJI' | 'FILLER' | 'DIGIT' | 'CHAR';
export type Locale = 'en' | 'es';

/**
 * Spanish connector words that add readability but minimal security.
 * These are common function words used to connect ideas.
 */
const SPANISH_FILLERS = new Set([
  'SE', 'Y', 'EN', 'DE', 'DEL', 'LA', 'EL', 'A', 'QUE', 'CON', 'POR',
  'LAS', 'LOS', 'UN', 'UNA', 'AL', 'ES', 'SU', 'SUS', 'MÁS', 'MAS',
  'PARA', 'COMO', 'PERO', 'POR', 'SIN', 'SOBRE', 'TAMBIÉN', 'TAMBIEN'
]);

/**
 * English connector words that add readability but minimal security.
 * Common articles, prepositions, and conjunctions.
 */
const ENGLISH_FILLERS = new Set([
  'THE', 'AND', 'OF', 'IN', 'TO', 'FOR', 'ON', 'AT', 'A', 'AN',
  'IS', 'WAS', 'ARE', 'BE', 'BY', 'WITH', 'FROM', 'AS', 'OR',
  'BUT', 'NOT', 'SO', 'CAN', 'IF', 'THIS', 'THAT', 'IT', 'HE', 'SHE'
]);

/**
 * Get the filler set for a given locale.
 */
function getFillerSet(locale: Locale): Set<string> {
  return locale === 'es' ? SPANISH_FILLERS : ENGLISH_FILLERS;
}

/**
 * Check if a string contains emoji characters.
 * Uses Unicode ranges for common emoji blocks.
 */
function containsEmoji(str: string): boolean {
  // Emoji ranges (simplified, covers most common emoji)
  const emojiRegex = /[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{FE00}-\u{FE0F}\u{1F1E0}-\u{1F1FF}]/u;
  return emojiRegex.test(str);
}

/**
 * Classify a single token into its type category.
 *
 * @param token - The token to classify (should be pre-trimmed)
 * @param locale - Language locale for filler detection
 * @returns The token type classification
 *
 * @example
 * classifyToken('hello', 'en') // 'WORD'
 * classifyToken('Y', 'es') // 'FILLER'
 * classifyToken('🚀', 'en') // 'EMOJI'
 * classifyToken('123', 'en') // 'DIGIT'
 * classifyToken('!', 'en') // 'CHAR'
 */
export function classifyToken(token: string, locale: Locale): TokenType {
  if (!token) return 'CHAR';

  // Emoji detection takes precedence
  if (containsEmoji(token)) {
    return 'EMOJI';
  }

  // Check if all characters are digits
  if (/^\d+$/.test(token)) {
    return 'DIGIT';
  }

  // Single non-alphanumeric character
  if (token.length === 1 && !/[a-zA-Z0-9]/.test(token)) {
    return 'CHAR';
  }

  // Check if token is a connector/filler word
  const upperToken = token.toUpperCase();
  const fillerSet = getFillerSet(locale);

  if (fillerSet.has(upperToken)) {
    return 'FILLER';
  }

  // Default to WORD for anything else
  return 'WORD';
}

/**
 * Tokenize a passphrase string into individual tokens.
 * Preserves emoji as single tokens, splits on whitespace and common separators.
 *
 * @param passphrase - The passphrase to tokenize
 * @returns Array of token strings
 *
 * @example
 * tokenize('my cat 🐈 runs fast')
 * // ['my', 'cat', '🐈', 'runs', 'fast']
 *
 * tokenize('👦🏼-🐈-SE-🗣️-BRULA')
 * // ['👦🏼', '🐈', 'SE', '🗣️', 'BRULA']
 */
export function tokenize(passphrase: string): string[] {
  if (!passphrase) return [];

  // Normalize to NFC form
  const normalized = passphrase.normalize('NFC');

  // Split on whitespace, hyphens, underscores, but preserve emoji
  // First, protect emoji by replacing them with placeholders
  const emojiMap: Map<string, string> = new Map();
  let emojiCounter = 0;

  let protectedStr = normalized.replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{FE00}-\u{FE0F}\u{1F1E0}-\u{1F1FF}][\u{FE00}-\u{FE0F}]?/gu, (match) => {
    const placeholder = `__EMOJI_${emojiCounter++}__`;
    emojiMap.set(placeholder, match);
    return placeholder;
  });

  // Split on whitespace, hyphens, underscores
  const rawTokens = protectedStr.split(/[\s\-_]+/).filter(t => t.length > 0);

  // Restore emoji
  const tokens = rawTokens.map(token => {
    if (emojiMap.has(token)) {
      return emojiMap.get(token)!;
    }
    return token;
  });

  return tokens.filter(t => t.length > 0);
}

/**
 * Tokenize and classify a passphrase in one operation.
 *
 * @param passphrase - The passphrase to analyze
 * @param locale - Language locale for filler detection
 * @returns Array of tokens with their classifications
 *
 * @example
 * tokenizeAndClassify('my cat 🐈', 'en')
 * // [
 * //   { value: 'my', type: 'WORD' },
 * //   { value: 'cat', type: 'WORD' },
 * //   { value: '🐈', type: 'EMOJI' }
 * // ]
 */
export function tokenizeAndClassify(
  passphrase: string,
  locale: Locale
): Array<{ value: string; type: TokenType }> {
  const tokens = tokenize(passphrase);
  return tokens.map(value => ({
    value,
    type: classifyToken(value, locale),
  }));
}
